/**
 * Copyright © Smartex Canada All rights reserved.
 * See LICENSE for license details.
 */

import type { PriceFormatOptions } from "@/types";

/**
 * Generate random string of specified length
 */
export function uniqueId(size: number = 7): string {
    let code = Math.random() * 25 + 65 | 0,
        idstr = String.fromCharCode(code);

    while (idstr.length < size) {
        code = Math.floor(Math.random() * 42 + 48);

        if (code < 58 || code > 64) {
            idstr += String.fromCharCode(code);
        }
    }

    return idstr;
}

const replace: Record<string, string> = { '+': '-', '/': '_', '=': ',' };
export function getUenc(): string {
    return btoa(location.href).replace(/[+/=]/g, match => replace[match]);
}

/**
 * Format price
 */
export function formatPrice(value: number, showSign?: boolean, options: PriceFormatOptions = {}): string {
    options = {...window.themeConfig.priceFormat, ...options};

    const groupSeparator = options.groupSeparator;
    const decimalSeparator = options.decimalSeparator;

    delete options.groupSeparator;
    delete options.decimalSeparator;

    const formatter = new Intl.NumberFormat(
        window.LOCALE,
        Object.assign({
            style: 'currency',
            signDisplay: showSign ? 'always' : 'auto'
        }, options)
    );

    if (typeof Intl.NumberFormat.prototype.formatToParts === 'function') {
        return formatter.formatToParts(value)
            .map(({type, value}) => {
                switch (type) {
                    case 'currency':
                        return window.themeConfig.currencySymbol || value;
                    case 'minusSign':
                        return '- ';
                    case 'plusSign':
                        return '+ ';
                    case 'group':
                        return groupSeparator !== undefined ? groupSeparator : value;
                    case 'decimal':
                        return decimalSeparator !== undefined ? decimalSeparator : value;
                    default :
                        return value;
                }
            })
            .reduce((string, part) => string + part);
    }

    return formatter.format(value);
}

/**
 * Get all path param pairs from url or BASE_URL
 * (pairs after BASE_URL/front_name/action_name/action)
 */
export function getUrlPathParams(url?: string): Record<string, string> {
    const baseUrl = (window.BASE_URL.substring(0, 2) === '//' ? 'http:' : '') + window.BASE_URL;
    const baseUrlParts = (new URL(baseUrl)).pathname.replace(/\/$/, '').split('/');
    const pathParts = (url || location.pathname).split('/').slice(baseUrlParts.length + 3);
    const result: Record<string, string> = {};

    for (let i = 0; i < pathParts.length; i += 2) {
        if (pathParts.length > i) {
            result[pathParts[i]] = pathParts[i + 1];
        }
    }

    return result;
}

/**
 * Get search params from url or BASE_URL
 */
export function getUrlSearchParams(url?: string): URLSearchParams {
    return (new URL(url || window.BASE_URL)).searchParams;
}
